import { createApiClient, type PlatformAppInfo, type ServerEntry } from '@sd/client-core';
import { api, type Me } from '@sd/contracts';
import type { DesktopLoginResult, ServerVerification } from '@sd/contracts/ipc';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Icon } from '../../icons/Icon';
import { ServerBadge } from '../../ui/Avatar';
import { Button, Field, Notice } from '../../ui/controls';
import { isComposing } from '../../ui/keys';
import { useToast } from '../../ui/overlays';
import type { ConnectScreenProps, ConnectedServer } from '../types';
import { errorMessage } from '../../errors';
import { RecoveryCodes, TotpSetup } from '../shared/MfaParts';
import { OtpInput } from '../shared/OtpInput';
import { Stepper, WindowTitlebar } from '../shared/parts';
import { hostOf } from '../../format';
import { ConnectBrand } from './ConnectBrand';
import { VerifyResult } from './VerifyResult';

type Step = 'address' | 'verify' | 'login' | 'mfa' | 'mfa-setup' | 'recovery' | 'done';

const DESKTOP_STEPS = ['주소', '서버 확인', '로그인', '2단계 인증'] as const;
const WEB_STEPS = ['로그인', '2단계 인증'] as const;

function stepNumber(step: Step, desktop: boolean): number {
  const order: Record<Step, number> = { address: 1, verify: 2, login: 3, mfa: 4, 'mfa-setup': 4, recovery: 4, done: 5 };
  return desktop ? order[step] : Math.max(1, order[step] - 2);
}

/**
 * Said by step 2's status line, which stays in place while the check runs so its result is announced.
 * A failure is announced by VerifyResult as an alert.
 */
function verifyAnnouncement(verification: ServerVerification | null): string {
  if (!verification) return '서버를 확인하는 중…';
  if (!verification.ok) return '';
  return verification.compatible ? `${verification.companyName} · ${hostOf(verification.serverUrl)} 서버를 확인했어요` : '이 앱 버전으로는 이 서버에 연결할 수 없어요';
}

/** Challenge tokens are short-lived; these codes mean "start the login again". */
const CHALLENGE_GONE = new Set(['UNAUTHENTICATED', 'TOKEN_EXPIRED', 'SESSION_REVOKED']);

/**
 * Company server connection: verify the address (HTTPS, identity,
 * compatibility) before registering it, then sign in with the instance-local
 * account and TOTP. Web mode only signs in to the server it is served from.
 */
export function ConnectScreen({ platform, mode, server, initialAddress, onConnected, onCancel }: ConnectScreenProps) {
  const desktop = mode === 'desktop';
  const toast = useToast();
  const [step, setStep] = useState<Step>(desktop ? 'address' : 'login');
  const [info, setInfo] = useState<PlatformAppInfo | null>(null);
  const [servers, setServers] = useState<ServerEntry[]>([]);
  const [address, setAddress] = useState(initialAddress ?? '');
  const [addressError, setAddressError] = useState<string | null>(null);
  const [verification, setVerification] = useState<ServerVerification | null>(null);
  const [target, setTarget] = useState<ConnectedServer | null>(server ?? null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [challenge, setChallenge] = useState<string | null>(null);
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpKey, setOtpKey] = useState(0);
  const [setup, setSetup] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  /** The server sends no mail: a forgotten password is a link from a company admin (ADR-0007). */
  const [mailOff, setMailOff] = useState(false);
  const addressRef = useRef<HTMLInputElement>(null);
  const cardRef = useRef<HTMLElement>(null);

  // A step that replaced the focused control (Enter in the address field, 이전) leaves focus on the page body,
  // where a screen reader says nothing about the new step: start it at its heading.
  useEffect(() => {
    const card = cardRef.current;
    if (!card || card.contains(document.activeElement)) return;
    card.querySelector<HTMLElement>('.step h2')?.focus();
  }, [step]);

  useEffect(() => {
    let alive = true;
    platform.appInfo().then((value) => alive && setInfo(value), () => undefined);
    if (desktop) platform.listServers().then((value) => alive && setServers(value), () => undefined);
    return () => {
      alive = false;
    };
  }, [platform, desktop]);

  useEffect(() => {
    if (!desktop || !initialAddress) return;
    setAddress(initialAddress);
    setStep('address');
  }, [desktop, initialAddress]);

  useEffect(() => {
    if (server) setTarget(server);
  }, [server]);

  function resetMfa() {
    setChallenge(null);
    setUseRecovery(false);
    setRecoveryCode('');
    setOtpCode('');
    setOtpKey((key) => key + 1);
    setSetup(null);
  }

  async function verify(value = address) {
    const input = value.trim();
    if (!input) {
      setAddressError('서버 주소를 입력해 주세요');
      addressRef.current?.focus();
      return;
    }
    if (!platform.serverRegistry) return;
    setAddressError(null);
    setVerification(null);
    setStep('verify');
    setBusy(true);
    try {
      setVerification(await platform.serverRegistry.verify(input));
    } catch {
      setVerification({ ok: false, reason: 'unreachable', detail: null });
    } finally {
      setBusy(false);
    }
  }

  async function openSignedIn(entry: ConnectedServer) {
    setBusy(true);
    try {
      const signedIn = await createApiClient(platform.transport(entry.serverId)).call(api.users.me);
      await platform.setActiveServer(entry.serverId);
      onConnected({ server: entry, me: signedIn });
    } catch {
      // Session is gone or the server is unreachable: sign in again.
      setTarget(entry);
      setError(null);
      setStep('login');
    } finally {
      setBusy(false);
    }
  }

  async function connectVerified() {
    if (!verification?.ok || !verification.compatible || !platform.serverRegistry) return;
    setBusy(true);
    try {
      let entry: ConnectedServer;
      if (verification.alreadyRegistered) {
        entry = { serverId: verification.instanceId, companyName: verification.companyName, serverUrl: verification.serverUrl };
        const existing = servers.find((s) => s.serverId === verification.instanceId);
        if (existing?.signedIn) {
          setBusy(false);
          await openSignedIn(entry);
          return;
        }
      } else {
        const added = await platform.serverRegistry.add(verification.verificationId);
        entry = { serverId: added.serverId, companyName: added.companyName, serverUrl: added.serverUrl };
        setServers(await platform.listServers().catch(() => servers));
      }
      setTarget(entry);
      setError(null);
      setStep('login');
    } catch (cause) {
      toast(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  function finish(user: Me) {
    if (!target) return;
    if (desktop) {
      setMe(user);
      setStep('done');
      return;
    }
    onConnected({ server: target, me: user });
  }

  async function openMessenger() {
    if (!target || !me) return;
    await platform.setActiveServer(target.serverId).catch(() => undefined);
    onConnected({ server: target, me });
  }

  function handleResult(result: DesktopLoginResult, origin: 'login' | 'mfa') {
    switch (result.status) {
      case 'ok':
        if (result.recoveryCodes?.length) {
          setMe(result.me);
          setRecoveryCodes(result.recoveryCodes);
          setStep('recovery');
        } else {
          finish(result.me);
        }
        return;
      case 'mfa_required':
      case 'mfa_setup_required':
        resetMfa();
        setChallenge(result.challengeToken);
        setError(null);
        setStep(result.status === 'mfa_required' ? 'mfa' : 'mfa-setup');
        return;
      case 'error':
        if (origin === 'mfa' && CHALLENGE_GONE.has(result.code)) {
          resetMfa();
          setStep('login');
          setError('인증 시간이 지났어요. 다시 로그인해 주세요.');
          return;
        }
        setError(errorMessage(result.code));
        if (origin === 'mfa') {
          setOtpKey((key) => key + 1);
          setOtpCode('');
        }
    }
  }

  async function login(event?: FormEvent) {
    event?.preventDefault();
    if (!target || busy) return;
    if (!email.trim() || !password) {
      setError('이메일과 비밀번호를 입력해 주세요.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      handleResult(await platform.auth.login(target.serverId, email.trim(), password), 'login');
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  async function submitMfa(code = otpCode) {
    if (!target || !challenge || busy) return;
    const input = useRecovery ? { recoveryCode: recoveryCode.trim() } : { code };
    if (useRecovery ? !recoveryCode.trim() : !/^\d{6}$/.test(code)) {
      setError(useRecovery ? '복구 코드를 입력해 주세요.' : '6자리 코드를 모두 입력해 주세요.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      handleResult(await platform.auth.verifyMfa(target.serverId, challenge, input), 'mfa');
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (step !== 'mfa-setup' || !challenge || !target || setup) return undefined;
    let alive = true;
    platform.auth.startMfaSetup(target.serverId, challenge).then(
      (value) => alive && setSetup(value),
      (cause: unknown) => alive && setError(errorMessage(cause)),
    );
    return () => {
      alive = false;
    };
  }, [step, challenge, target, setup, platform]);

  async function confirmSetup(code: string) {
    if (!target || !challenge || busy) return;
    setBusy(true);
    setError(null);
    try {
      handleResult(await platform.auth.confirmMfaSetup(target.serverId, challenge, code), 'mfa');
    } catch (cause) {
      setError(errorMessage(cause));
      setOtpKey((key) => key + 1);
    } finally {
      setBusy(false);
    }
  }

  // Public and cheap; on failure the sign-in page keeps offering the reset mail, as before.
  useEffect(() => {
    if (step !== 'login' || !target) return undefined;
    let alive = true;
    createApiClient(platform.transport(target.serverId))
      .call(api.meta.serverInfo)
      .then(
        (info) => alive && setMailOff(info.mail.status === 'not_configured'),
        () => alive && setMailOff(false),
      );
    return () => {
      alive = false;
    };
  }, [step, target, platform]);

  async function requestReset() {
    if (!target) return;
    if (mailOff) {
      toast('이 회사 서버는 메일을 보내지 않아요. 회사 관리자에게 비밀번호 재설정 링크를 요청하세요.');
      return;
    }
    const value = email.trim();
    if (!value) {
      toast('비밀번호를 재설정할 이메일을 먼저 입력해 주세요');
      return;
    }
    try {
      await createApiClient(platform.transport(target.serverId)).call(api.auth.requestPasswordReset, { body: { email: value } });
      toast('비밀번호 재설정 메일을 요청했어요. 메일은 회사 서버에 연결된 메일 서비스로 발송돼요.');
    } catch (cause) {
      toast(errorMessage(cause));
    }
  }

  const weakStorage = desktop && (info?.secureStorage === 'weak' || info?.secureStorage === 'unavailable');
  // On Linux "unavailable" is a key store that is there but did not answer when the app started: locked (the
  // GNOME keyring or KWallet after an automatic login, its password not typed in time) or a KWallet not created
  // yet. The app keeps that answer until it is started again, so the way back is a restart.
  const lockedKeyStore = info?.platform === 'linux' && info.secureStorage === 'unavailable';
  const steps = desktop ? DESKTOP_STEPS : WEB_STEPS;
  const host = target ? hostOf(target.serverUrl) : '';

  const errorNotice = error ? (
    <Notice tone="danger" icon="alertc" role="alert">
      {error}
    </Notice>
  ) : null;

  return (
    <section className="screen scrolly on" id="scr-connect" aria-label={desktop ? '회사 서버 연결' : '로그인'}>
      <div className="page">
        {desktop ? <WindowTitlebar titlebar={info?.titlebar ?? null} title="sd-messenger · 회사 서버 연결" /> : null}
        <div className="cx">
          <ConnectBrand mode={mode} companyName={target?.companyName ?? null} />
          <section ref={cardRef} className="cx-card glass" aria-label="연결 단계">
            <Stepper steps={steps} current={stepNumber(step, desktop)} />

            {step === 'address' ? (
              <div className="step on">
                <h2 tabIndex={-1}>회사 서버 주소</h2>
                <p className="lead">관리자에게 받은 주소나 초대 메일에 적힌 주소를 입력하세요.</p>
                <Field
                  ref={addressRef}
                  label="서버 주소"
                  icon="globe"
                  value={address}
                  placeholder="messenger.company.example"
                  autoComplete="off"
                  spellCheck={false}
                  error={Boolean(addressError)}
                  hint={addressError ?? 'https:// 는 자동으로 붙어요. 가입을 마친 화면이나 다운로드 페이지에서 ‘앱에서 열기’를 누르면 자동으로 입력돼요.'}
                  onChange={(event) => setAddress(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !isComposing(event)) {
                      event.preventDefault();
                      void verify();
                    }
                  }}
                />
                {servers.length ? (
                  <div>
                    <div className="sec-h" style={{ margin: '4px 6px 6px' }}>
                      이 앱에 등록된 회사
                    </div>
                    {servers.map((entry) => (
                      <button
                        key={entry.serverId}
                        type="button"
                        className="prow"
                        disabled={busy}
                        onClick={() => {
                          if (entry.signedIn) {
                            void openSignedIn({ serverId: entry.serverId, companyName: entry.companyName, serverUrl: entry.serverUrl });
                            return;
                          }
                          setAddress(hostOf(entry.serverUrl));
                          addressRef.current?.focus();
                        }}
                      >
                        <ServerBadge serverId={entry.serverId} companyName={entry.companyName} style={{ width: 40, height: 40, borderRadius: 14 }} />
                        <span className="pr-main">
                          <span className="pr-name">{entry.companyName}</span>
                          <span className="pr-sub">
                            {hostOf(entry.serverUrl)} · {entry.signedIn ? '로그인됨' : '로그인 필요'}
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                ) : null}
                <div className="step-nav">
                  {onCancel ? (
                    <Button variant="ghost" onClick={onCancel}>
                      취소
                    </Button>
                  ) : null}
                  <span className="grow" />
                  <Button variant="primary" size="lg" onClick={() => void verify()} disabled={!platform.serverRegistry}>
                    서버 확인
                    <Icon name="right" size="sm" />
                  </Button>
                </div>
              </div>
            ) : null}

            {step === 'verify' ? (
              <div className="step on">
                <h2 tabIndex={-1}>이 서버가 맞나요?</h2>
                <p className="lead">연결하기 전에 회사 이름과 주소를 확인하세요.</p>
                <p className="sr" role="status">{verifyAnnouncement(verification)}</p>
                <VerifyResult address={address} verification={verification} appVersion={info?.appVersion ?? null} onRetry={() => void verify()} />
                <div className="step-nav">
                  <Button variant="ghost" onClick={() => setStep('address')}>
                    이전
                  </Button>
                  <span className="grow" />
                  <Button
                    variant="primary"
                    size="lg"
                    disabled={busy || !verification?.ok || !verification.compatible}
                    onClick={() => void connectVerified()}
                  >
                    {!verification ? '확인하는 중…' : verification.ok && verification.compatible ? '이 회사 서버에 연결' : '연결할 수 없어요'}
                  </Button>
                </div>
              </div>
            ) : null}

            {step === 'login' && target ? (
              <form className="step on" onSubmit={(event) => void login(event)} noValidate>
                <h2 tabIndex={-1}>{target.companyName}에 로그인</h2>
                <p className="lead">{host} 계정으로 로그인해요. 클라우드 콘솔 계정과는 별개예요.</p>
                <Field
                  label="이메일"
                  icon="mail"
                  type="email"
                  value={email}
                  autoComplete="username"
                  onChange={(event) => setEmail(event.target.value)}
                  error={Boolean(error)}
                  autoFocus
                />
                <Field
                  label="비밀번호"
                  icon="lock"
                  type="password"
                  value={password}
                  autoComplete="current-password"
                  onChange={(event) => setPassword(event.target.value)}
                  error={Boolean(error)}
                  hint={
                    mailOff ? (
                      '비밀번호를 잊었다면 회사 관리자에게 재설정 링크를 요청하세요.'
                    ) : (
                      <>
                        <button type="button" className="linkbtn" onClick={() => void requestReset()}>
                          비밀번호를 잊으셨나요?
                        </button>{' '}
                        재설정 메일은 회사 서버에 연결된 메일 서비스로 발송돼요.
                      </>
                    )
                  }
                />
                {errorNotice}
                {weakStorage ? (
                  <Notice tone="warn" icon="alert">
                    {lockedKeyStore
                      ? '이 PC의 키 저장소(GNOME 키 모음·KDE 지갑)를 열지 못해 자동 로그인과 오프라인 메시지 저장이 꺼져 있어요. 키 저장소의 암호를 입력했거나 지갑을 새로 만들었다면 앱을 완전히 종료하고 다시 실행하세요.'
                      : '이 PC에서 안전한 비밀 저장소를 찾지 못해 자동 로그인과 오프라인 메시지 저장이 꺼져 있어요. 앱을 닫으면 다시 로그인해야 해요.'}
                  </Notice>
                ) : null}
                <div className="step-nav">
                  {desktop ? (
                    <Button variant="ghost" onClick={() => setStep(verification ? 'verify' : 'address')}>
                      이전
                    </Button>
                  ) : null}
                  <span className="grow" />
                  <Button type="submit" variant="primary" size="lg" disabled={busy}>
                    {busy ? '로그인하는 중…' : '로그인'}
                  </Button>
                </div>
              </form>
            ) : null}

            {step === 'mfa' ? (
              <div className="step on">
                <h2 tabIndex={-1}>2단계 인증</h2>
                <p className="lead">
                  {useRecovery ? '복구 코드 하나를 입력하세요. 쓴 코드는 다시 쓸 수 없어요.' : '인증 앱의 6자리 코드를 입력하세요. 회사 관리자 계정은 2단계 인증이 꼭 필요해요.'}
                </p>
                {useRecovery ? (
                  <Field
                    label="복구 코드"
                    icon="key"
                    value={recoveryCode}
                    placeholder="XXXX-XXXX-XXXX"
                    autoComplete="one-time-code"
                    spellCheck={false}
                    autoFocus
                    onChange={(event) => setRecoveryCode(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !isComposing(event)) {
                        event.preventDefault();
                        void submitMfa();
                      }
                    }}
                  />
                ) : (
                  <OtpInput key={otpKey} onChange={setOtpCode} onComplete={(code) => void submitMfa(code)} disabled={busy} autoFocus />
                )}
                {errorNotice}
                <button
                  type="button"
                  className="linkbtn"
                  style={{ alignSelf: 'flex-start' }}
                  onClick={() => {
                    setUseRecovery((value) => !value);
                    setError(null);
                  }}
                >
                  {useRecovery ? '인증 앱 코드로 입력하기' : '인증 앱을 쓸 수 없나요? 복구 코드 사용'}
                </button>
                <div className="step-nav">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      resetMfa();
                      setError(null);
                      setStep('login');
                    }}
                  >
                    이전
                  </Button>
                  <span className="grow" />
                  <Button variant="primary" size="lg" disabled={busy} onClick={() => void submitMfa()}>
                    {busy ? '확인하는 중…' : '확인'}
                  </Button>
                </div>
              </div>
            ) : null}

            {step === 'mfa-setup' ? (
              <div className="step on">
                <h2 tabIndex={-1}>2단계 인증 설정</h2>
                <p className="lead">회사 소유자·관리자 계정은 처음 로그인할 때 인증 앱을 등록해야 해요.</p>
                {setup ? (
                  <TotpSetup secret={setup.secret} otpauthUrl={setup.otpauthUrl} busy={busy} resetKey={otpKey} onCode={(code) => void confirmSetup(code)} />
                ) : !error ? (
                  <p className="hint" role="status">
                    인증 키를 준비하는 중…
                  </p>
                ) : null}
                {errorNotice}
                <div className="step-nav">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      resetMfa();
                      setError(null);
                      setStep('login');
                    }}
                  >
                    이전
                  </Button>
                </div>
              </div>
            ) : null}

            {step === 'recovery' && recoveryCodes && me ? (
              <div className="step on">
                <h2 tabIndex={-1}>복구 코드를 보관하세요</h2>
                <RecoveryCodes
                  codes={recoveryCodes}
                  continueLabel={desktop ? '계속' : '메신저 열기'}
                  onContinue={() => {
                    setRecoveryCodes(null);
                    finish(me);
                  }}
                />
              </div>
            ) : null}

            {step === 'done' && target ? (
              <div className="step on">
                <div className="iv-done">
                  <div className="big">
                    <Icon name="check" size="lg" style={{ width: 36, height: 36 }} />
                  </div>
                  <h2 tabIndex={-1}>연결됐어요!</h2>
                  <p className="lead" style={{ margin: 0 }}>
                    {target.companyName} · {host}
                    <br />
                    다른 회사 서버는 왼쪽 레일의 ＋에서 추가할 수 있어요.
                  </p>
                  <Button variant="primary" size="lg" block onClick={() => void openMessenger()} autoFocus>
                    메신저 열기
                  </Button>
                </div>
              </div>
            ) : null}

            {step === 'login' && !target ? (
              <Notice tone="danger" icon="alertc">
                연결할 회사 서버 정보를 불러오지 못했어요. 페이지를 새로고침해 주세요.
              </Notice>
            ) : null}
          </section>
        </div>
      </div>
    </section>
  );
}
